import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // 滚动到页面顶部
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span>返回</span>
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Privacy Policy</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* 标题部分 */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">
              Protecting your privacy is crucial to us. This Privacy Policy details how we collect, use, store, and protect your personal information.
            </p>
          </div>

          {/* 公司信息 */}
          <div className="mb-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Information</h2>
            <div className="space-y-2 text-gray-700">
              <p><span className="font-medium">Company Name:</span> SmartTool AI Solutions Limited</p>
              <p><span className="font-medium">Brand Name:</span> AI Monkey</p>
              <p><span className="font-medium">Company Website:</span> <a href="https://ai2you.org/" className="text-orange-500 hover:text-orange-600">https://ai2you.org/</a></p>
              <p><span className="font-medium">Contact Email:</span> <a href="mailto:support@ai2you.org" className="text-orange-500 hover:text-orange-600">support@ai2you.org</a></p>
              <p className="text-sm text-gray-600 mt-4">Effective Date: May 15, 2025</p>
            </div>
          </div>

          {/* 隐私政策内容 */}
          <div className="space-y-8">
            {/* 1. Information Collection */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information Collection</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">1.1 Information You Provide</h3>
                  <p className="text-gray-700 mb-2">
                    When you register an account, complete forms, use our services, or communicate with us, we collect the following types of information:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Identity information (such as name, username)</li>
                    <li>Contact information (such as email address, phone number)</li>
                    <li>Account credentials (such as passwords, security questions)</li>
                    <li>Payment information (such as credit card or bank account details)</li>
                    <li>Content you upload or create through our services</li>
                    <li>Your AI conversations, prompts, and generated content</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">1.2 Automatically Collected Information</h3>
                  <p className="text-gray-700 mb-2">
                    When you visit or use our services, we automatically collect:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Device information (such as IP address, browser type, operating system)</li>
                    <li>Usage data (such as pages visited, links clicked, features used)</li>
                    <li>Location information (if permitted)</li>
                    <li>Information collected through cookies and similar technologies</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">1.3 Information from Third Parties</h3>
                  <p className="text-gray-700">
                    We may receive information about you from third-party service providers, partners, or public sources to supplement the information we collect directly from you.
                  </p>
                </div>
              </div>
            </section>

            {/* 2. Information Use */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information Use</h2>
              <p className="text-gray-700 mb-4">We use the collected information for the following purposes:</p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">2.1 Providing Services</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Creating and managing your account</li>
                    <li>Providing, personalizing, and improving our AI tools and services</li>
                    <li>Processing your payments and subscriptions</li>
                    <li>Training and fine-tuning our AI models to improve responses</li>
                    <li>Implementing features and operations you request</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">2.2 Communications</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Responding to your inquiries and requests</li>
                    <li>Sending service-related notifications and updates</li>
                    <li>Providing support and assistance</li>
                    <li>Sending marketing and promotional information (with your consent)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">2.3 Analysis and Improvement</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Analyzing user behavior and trends</li>
                    <li>Monitoring and improving our service performance and functionality</li>
                    <li>Developing new AI features and services</li>
                    <li>Conducting research and data analysis</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">2.4 Security and Legal Compliance</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Protecting our services against misuse and unauthorized access</li>
                    <li>Preventing fraud and abuse</li>
                    <li>Fulfilling our legal obligations</li>
                    <li>Resolving disputes and enforcing our agreements</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 3. Information Sharing */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information Sharing</h2>
              <p className="text-gray-700 mb-4">We may share your information in the following circumstances:</p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">3.1 Service Providers</h3>
                  <p className="text-gray-700">
                    We share information with third-party service providers who help us operate, provide, and improve our services, such as payment processors, cloud storage providers, analytics services, etc. These providers only process your information according to our instructions and are bound by contractual obligations to protect your information.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">3.2 Partners</h3>
                  <p className="text-gray-700">
                    With your consent, we may share your information with our business partners to offer you products or services or participate in joint marketing activities.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">3.3 Legal Requirements</h3>
                  <p className="text-gray-700 mb-2">We may disclose your information if required by law or for the following purposes:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Complying with legal processes or government requests</li>
                    <li>Enforcing our terms and policies</li>
                    <li>Protecting our rights, property, or safety, or those of others</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">3.4 Business Transfers</h3>
                  <p className="text-gray-700">
                    If we are involved in a merger, acquisition, or asset sale, your information may be transferred as part of that transaction. We will notify you of such transfers and outline your choices.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">3.5 Anonymized and Aggregated Information</h3>
                  <p className="text-gray-700">
                    We may share anonymized or aggregated information that cannot reasonably be used to identify you.
                  </p>
                </div>
              </div>
            </section>

            {/* 4. Data Security */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We are committed to protecting your personal information. We have implemented appropriate technical and organizational measures to prevent your information from being lost, misused, or subjected to unauthorized access, disclosure, alteration, or destruction.
              </p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">4.1 Security Measures</h3>
                  <p className="text-gray-700 mb-2">Our security measures include but are not limited to:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Using encryption technologies to protect data transmission and storage</li>
                    <li>Implementing access controls to limit access to personal information</li>
                    <li>Regular security assessments and audits</li>
                    <li>Employee privacy and security training</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">4.2 Data Breaches</h3>
                  <p className="text-gray-700">
                    In the event of a data breach, we will promptly notify you and relevant regulatory authorities as required by applicable law.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">4.3 Your Responsibility</h3>
                  <p className="text-gray-700">
                    It is your responsibility to keep your account credentials secure. We recommend using strong passwords, not sharing them with others, and logging out of your account after using shared or public devices.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">4.4 Third-Party Risks</h3>
                  <p className="text-gray-700">
                    While we take measures to protect your information, no method of transmitting information over the internet or storing information online is completely secure. We cannot guarantee that your information will not be intercepted by unauthorized third parties during transmission.
                  </p>
                </div>
              </div>
            </section>

            {/* 5. Your Rights and Choices */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">Depending on your region, you may have the following rights under data protection laws:</p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.1 Right to Access</h3>
                  <p className="text-gray-700">
                    You have the right to request access to the personal information we hold about you.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.2 Right to Rectification</h3>
                  <p className="text-gray-700">
                    If you believe the information we hold about you is inaccurate or incomplete, you have the right to request corrections.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.3 Right to Erasure</h3>
                  <p className="text-gray-700">
                    In certain circumstances, you have the right to request the deletion of your personal information (also known as the "right to be forgotten").
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.4 Right to Restrict Processing</h3>
                  <p className="text-gray-700">
                    In certain circumstances, you have the right to request the restriction of processing of your personal information.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.5 Right to Data Portability</h3>
                  <p className="text-gray-700">
                    You have the right to receive the personal information you have provided to us in a structured, commonly used, and machine-readable format, and to transmit this data to another controller.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.6 Right to Object</h3>
                  <p className="text-gray-700">
                    In certain circumstances, you have the right to object to our processing of your personal information.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.7 Right to Withdraw Consent</h3>
                  <p className="text-gray-700">
                    If we process your information based on your consent, you have the right to withdraw your consent at any time.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.8 How to Exercise Your Rights</h3>
                  <p className="text-gray-700 mb-2">You can exercise your rights by:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Using the settings in your account</li>
                    <li>Contacting our privacy team via email at: <a href="mailto:support@ai2you.org" className="text-orange-500 hover:text-orange-600">support@ai2you.org</a></li>
                    <li>Sending mail to our office address</li>
                  </ul>
                  <p className="text-gray-700 mt-2">
                    We will respond to your request within 30 days, but may need extended time in some circumstances.
                  </p>
                </div>
              </div>
            </section>

            {/* 6. Cookies and Similar Technologies */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Cookies and Similar Technologies</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">6.1 Use of Cookies</h3>
                  <p className="text-gray-700">
                    We use cookies and similar technologies (such as web beacons, pixels, and local storage) to collect and store information about your use of our services. Cookies are small text files that websites may place on your device when you visit.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">6.2 Types of Cookies</h3>
                  <p className="text-gray-700 mb-2">We use the following types of cookies:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li><strong>Essential Cookies:</strong> These cookies are necessary for the website to function and cannot be switched off in our systems.</li>
                    <li><strong>Performance Cookies:</strong> These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site.</li>
                    <li><strong>Functional Cookies:</strong> These cookies enable the website to provide enhanced functionality and personalization.</li>
                    <li><strong>Advertising Cookies:</strong> These cookies may be set through our site by our advertising partners to build a profile of your interests or show relevant ads.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">6.3 Cookie Control</h3>
                  <p className="text-gray-700">
                    You can refuse, accept, or remove cookies through your browser settings. Note that disabling certain cookies may affect the availability and functionality of our services.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">6.4 Do Not Track Signals</h3>
                  <p className="text-gray-700">
                    We currently do not respond to browser "Do Not Track" signals.
                  </p>
                </div>
              </div>
            </section>

            {/* 7. Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our services are not directed to children under 16 years of age. We do not knowingly collect personal information from children under 16.
              </p>
              <p className="text-gray-700">
                If you are a parent or guardian and you believe your child has provided us with personal information without your consent, please contact us. If we discover that we have collected personal information from a child under 16 without verifiable parental consent, we will take steps to delete that information from our servers.
              </p>
            </section>

            {/* 8. International Data Transfers */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Our servers are located in various global regions, and we may transfer your personal information to other countries around the world for the purposes described in this Privacy Policy.
              </p>
              <p className="text-gray-700 mb-4">
                When we transfer your personal information to other countries, we will implement appropriate safeguards to ensure that your data is adequately protected and in compliance with applicable data protection laws. These safeguards may include entering into standard contractual clauses with recipients or ensuring that recipients are certified under specific data protection frameworks.
              </p>
              <p className="text-gray-700">
                In some cases, we may need to disclose your personal information to foreign jurisdictions to comply with applicable legal requirements.
              </p>
            </section>

            {/* 9. Policy Changes */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Policy Changes</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons.
              </p>
              <p className="text-gray-700 mb-4">
                When we make significant changes to the Privacy Policy, we will notify you by posting the updated policy on our website and, where appropriate, by email. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
              </p>
              <p className="text-gray-700">
                Your continued use of our services after the Privacy Policy has been updated constitutes your acceptance of the modified policy.
              </p>
            </section>

            {/* 10. Contact Us */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg space-y-2 text-gray-700">
                <p><span className="font-medium">Email:</span> <a href="mailto:support@ai2you.org" className="text-orange-500 hover:text-orange-600">support@ai2you.org</a></p>
                <p><span className="font-medium">Mailing Address:</span> Hong Kong Kowloon Garden Street 2-16, Haokeng Commercial Center, Room 502C</p>
                <p><span className="font-medium">Company:</span> SmartTool AI Solutions Limited</p>
                <p className="text-sm text-gray-600 mt-4">
                  Our Data Protection Officer can be contacted at the above address, marked "For the attention of the Data Protection Officer."
                </p>
              </div>
              
              <p className="text-gray-700 mt-4">
                If you have any concerns about how we process your personal information, we hope you will contact us first, and we will work to resolve your issue. However, you also have the right to complain to your country's data protection regulatory authority.
              </p>
            </section>

            {/* 最后更新时间 */}
            <div className="mt-12 pt-8 border-t border-gray-200 text-center">
              <p className="text-gray-600 text-sm">
                <strong>Last Updated:</strong> May 15, 2025
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}